import { EditOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../../../contexts/Staffing/UserSettingsContext";
import { FONT_SIZE, NEUTRAL } from "../../../../theme/tokens";
import { censusReplacementSummaryLines } from "../../../../utils/censusReplacement";
import { byDimensionsOf } from "../../../../utils/censusResolution";
import { ChangesSource, type Day, type DayItem } from "../../../../utils/models";
import { dayCardStaffCount } from "../../../../utils/pastDayStats";
import { getCensus, getCensusSourceLabel, getCensusSourceName, getShiftCensus } from "../../../../utils/utilFunctions";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { CensusDisplay } from "../../../staffingBlock/CensusDisplay";
import { CensusBreakdownTooltipContent } from "../../../UIToolkit/Popups/CensusBreakdownTooltipContent";
import { ThemedSvg } from "../../../UIToolkit/ThemedSvg";

interface IProps {
	dayItem: DayItem;
	onEditStaff: (e: React.MouseEvent<HTMLElement>) => void;
	dayObj?: Day;
}

export const DayControl: FC<IProps> = ({ dayItem, onEditStaff, dayObj }) => {
	const { hospitalData, chosenDepartment, isHospitalView, isHospitalDemo, getWeekIndexByDate, getDayIndexByDate } = useHospitalData();
	const { settings } = useUserSettingsContext();
	const censusSourceLabel = getCensusSourceLabel(chosenDepartment, isHospitalView || isHospitalDemo);
	const { t } = useTranslation();
	const staffCount = dayCardStaffCount(dayItem, chosenDepartment.department_id, DateTime.now());

	// One shared renderer for every census tooltip in the app — see CensusBreakdownTooltipContent.
	// This used to be ~70 lines of mode branching that had drifted from the other two copies.
	let tooltipContent: React.ReactNode = t("census.tooltip.census");
	if (dayObj) {
		const override = dayObj.census_override;
		const weekIndex = getWeekIndexByDate(dayObj.date);
		const dayIndex = getDayIndexByDate(dayObj.date);
		const pccCensus = getShiftCensus(hospitalData.weeks[weekIndex], 0, dayIndex);
		const summaryLines = [
			...(override
				? [
						{ label: t("census.tooltip.manual"), value: override.override_value },
						{ label: censusSourceLabel, value: pccCensus },
					]
				: [{ label: t("census.tooltip.census"), value: dayItem.dayStats.census }]),
			...censusReplacementSummaryLines(t, getCensusSourceName(chosenDepartment, isHospitalView || isHospitalDemo), dayObj.census_replacement),
		];

		tooltipContent = (
			<CensusBreakdownTooltipContent
				summaryLines={summaryLines}
				mode={settings.censusByLocation}
				byDimensions={byDimensionsOf(dayObj.census_grouping)}
				totalCensus={getCensus(hospitalData.weeks[weekIndex], dayIndex)}
				locations={chosenDepartment.locations}
				statsMode={chosenDepartment.statsMode}
			/>
		);
	}

	return (
		<Box sx={{ p: "8px" }} data-testid={`day-control-${dayItem.date.weekdayISO}`}>
			<Box
				sx={{
					bgcolor: NEUTRAL[50],
					display: "flex",
					justifyContent: "space-between",
					// Without this the row's `align-items` computes to `stretch`: each half grows to the full
					// 30px and centres its own contents independently, which put the two pencil icons -- same
					// glyph, same size, same row -- 3.8px apart.
					alignItems: "center",
					px: 1,
					height: "30px",
					"&:hover": {
						bgcolor: NEUTRAL[50],
					},
					transition: "background-color 0.2s ease",
				}}
			>
				<CensusDisplay
					source={ChangesSource.Staffing}
					dayDate={dayItem.date}
					censusValue={dayItem.dayStats?.census ?? 0}
					censusSx={{ fontWeight: 600 }}
					tooltipContent={tooltipContent}
				/>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
					<ThemedSvg src="/staff.svg" alt="Nurse" size={18} color="var(--inhouse-primary)" />
					<Typography data-testid={`day-staff-count-${dayItem.date.toISODate()}`} sx={{ color: "primary.main", fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS }}>
						{staffCount}
					</Typography>
					<InHouseTooltip title="Edit Staff">
						<IconButton
							data-testid={`day-control-${dayItem.date.weekdayISO}-shift-edit-button`}
							onClick={onEditStaff}
							sx={{
								padding: 0,
								marginLeft: "2px",
							}}
						>
							<EditOutlined
								sx={{
									fontSize: "16px",
									cursor: "pointer",
									color: "primary.main",
								}}
							/>
						</IconButton>
					</InHouseTooltip>
				</Box>
			</Box>
		</Box>
	);
};
